// One worker, one queue, one task: the pages every Workers row opens.
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ExternalLink, Gauge, Play, RefreshCw, RotateCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useCloudLogsQuery } from '@/client/api/cloud/cloud.query';
import {
  WorkerChange,
  useWorkerQuery,
  useWorkersQueueQuery,
  useWorkersRefresh,
  useWorkersTaskQuery,
} from '@/client/api/workers/workers.query';
import { Health } from '@/client/types/cloud';
import { Span, WorkerEnv, WorkerWindow } from '@/client/types/workers';
import {
  CLOUD_BASE,
  Card,
  EmptyRow,
  EnvBadge,
  Field,
  HealthPill,
  HealthReasons,
  MetricChart,
  PageShell,
  Tabs,
  Th,
  UsagePct,
  fmtAgo,
  fmtNum,
  fmtTime,
} from '../Cloud/cloudUi';
import {
  AccessDenied,
  AuditTable,
  ChangeDialog,
  ErrorNotice,
  FailureList,
  FetchingChip,
  FiguresCard,
  HeldTable,
  LoadingNote,
  QueueLink,
  RowMenu,
  RunsTable,
  TaskLink,
  Throughput,
  WINDOWS,
  WorkerLink,
  consumeItem,
  fmtFailRate,
  fmtMs,
  fmtSpan,
  seriesOf,
  shortTask,
  shortWorker,
  tabPath,
  useWorkerEnv,
  useWorkerPerms,
} from './workersUi';

const WINDOW_HOURS: Record<WorkerWindow, number> = { '6h': 6, '24h': 24, '7d': 168, '30d': 720 };

type Query = { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };

/** The frame every detail page shares: access, window, refresh, loading and errors. */
const Detail: React.FC<{
  title: string;
  description: string;
  crumb: string;
  tab: string;
  q: Query;
  window: WorkerWindow;
  onWindow: (w: WorkerWindow) => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, crumb, tab, q, window, onWindow, actions, children }) => {
  const [env] = useWorkerEnv();
  useBreadcrumbs([
    { label: 'Workers', href: tabPath('overview', env) },
    { label: crumb, href: tabPath(tab, env) },
    { label: title },
  ]);
  const perms = useWorkerPerms();
  const refresh = useWorkersRefresh();
  if (perms.loading || !perms.canView) {
    return (
      <PageShell title={title} description="">
        {perms.loading ? <Skeleton className="h-64 w-full" /> : <AccessDenied />}
      </PageShell>
    );
  }
  return (
    <PageShell
      title={title}
      description={description}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <FetchingChip />
          <EnvBadge env={env} />
          <Tabs value={window} onChange={onWindow} options={WINDOWS} />
          {actions}
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading ? (
        <>
          <LoadingNote />
          <Skeleton className="h-64 w-full" />
        </>
      ) : (
        children
      )}
    </PageShell>
  );
};

const HealthCard: React.FC<{ health: Health; ok: string }> = ({ health, ok }) => (
  <div className="flex flex-wrap items-start gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <HealthPill level={health.level} />
    {health.reasons.length ? (
      <HealthReasons health={health} className="text-sm" />
    ) : (
      <p className="text-sm text-gray-600">{ok}</p>
    )}
  </div>
);

/** A button that opens the confirmation for one change. */
const ChangeButton: React.FC<{
  env: WorkerEnv;
  change: WorkerChange;
  icon: React.ReactNode;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ env, change, icon, danger, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant={danger ? 'destructive' : 'default'} onClick={() => setOpen(true)}>
        {icon}
        {children}
      </Button>
      {open && <ChangeDialog env={env} change={change} onClose={() => setOpen(false)} />}
    </>
  );
};

const Charts: React.FC<{ span: Span }> = ({ span }) => (
  <div className="grid gap-3 xl:grid-cols-2">
    <Card title="Runs and failures" note={`last ${span.window}`}>
      <Throughput series={span.series} window={span.window} />
    </Card>
    <Card title="Average run time and queue wait" note="milliseconds">
      <MetricChart
        series={[
          ...seriesOf(span.series, 'runtime_ms', 'Run time'),
          ...seriesOf(span.series, 'wait_ms', 'Queue wait'),
        ]}
        spanHours={WINDOW_HOURS[span.window]}
      />
    </Card>
  </div>
);

const Totals: React.FC<{ span: Span }> = ({ span }) => (
  <>
    <Field label={`Runs, last ${span.window}`}>{fmtNum(span.totals.runs)}</Field>
    <Field label="Failed">
      {fmtNum(span.totals.failed)} ({fmtFailRate(span.totals)})
    </Field>
    <Field label="Retried">{fmtNum(span.totals.retried)}</Field>
    <Field label="Run time, avg / longest">
      {fmtMs(span.totals.runtime_avg_ms)} / {fmtMs(span.totals.runtime_max_ms)}
    </Field>
    <Field label="Queue wait, avg / longest">
      {fmtMs(span.totals.wait_avg_ms)} / {fmtMs(span.totals.wait_max_ms)}
    </Field>
  </>
);

/* ---------- worker ---------- */

const LogsCard: React.FC<{ appId: string; component: string }> = ({ appId, component }) => {
  const [lines, setLines] = useState(200);
  const q = useCloudLogsQuery({ appId, component, type: 'run', lines });
  return (
    <Card
      title="Log"
      note={`last ${lines} lines of ${component}, secrets masked`}
      action={
        <div className="flex items-center gap-2">
          <Tabs value={String(lines)} onChange={(v) => setLines(Number(v))} options={['200', '1000']} />
          <Button size="sm" variant="outline" disabled={q.isFetching} onClick={() => q.refetch()}>
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${q.isFetching ? 'animate-spin' : ''}`} />
            Reload
          </Button>
        </div>
      }
      flush
    >
      {q.isError ? (
        <div className="p-4">
          <ErrorNotice error={q.error} onRetry={() => q.refetch()} />
        </div>
      ) : q.isLoading ? (
        <Skeleton className="m-4 h-40" />
      ) : (
        <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap break-all bg-gray-950 p-4 font-mono text-xs leading-relaxed text-gray-100">
          {q.data?.text || 'The provider returned no log lines.'}
        </pre>
      )}
    </Card>
  );
};

export const WorkerDetailPage: React.FC = () => {
  const { name = '' } = useParams();
  const [env] = useWorkerEnv();
  const [window, setWindow] = useState<WorkerWindow>('24h');
  const perms = useWorkerPerms();
  const q = useWorkerQuery(env, name, window);
  const d = q.data;
  const w = d?.worker;
  const queues = w ? [...new Set([...(w.expected_queues ?? []), ...(w.queues ?? [])])].sort() : [];
  return (
    <Detail
      title={shortWorker(name)}
      description="One worker: whether it is healthy, what it holds, what it has done and what it logged."
      crumb="Fleet"
      tab="fleet"
      q={q}
      window={window}
      onWindow={setWindow}
      actions={
        perms.canOperate && w?.component ? (
          <ChangeButton
            env={env}
            change={{ kind: 'restart', component: w.component, worker: w.name }}
            icon={<RotateCw className="mr-2 h-3.5 w-3.5" />}
            danger
          >
            Restart
          </ChangeButton>
        ) : undefined
      }
    >
      {d && w && (
        <>
          <HealthCard health={w.health} ok="Answering, with nothing to report." />
          <Card
            title="Worker"
            action={
              w.app_id ? (
                <Link to={`${CLOUD_BASE}/apps/${w.app_id}`} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                  Scale or resize in Cloud <ExternalLink className="h-3 w-3" />
                </Link>
              ) : undefined
            }
          >
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
              <Field label="Node">{w.name}</Field>
              <Field label="Component">{w.component ?? 'not in the deployed spec'}</Field>
              <Field label="Size">{[w.size, w.instance_count && `x${w.instance_count}`].filter(Boolean).join(' ') || '—'}</Field>
              <Field label="Branch">{w.branch ?? '—'}</Field>
              <Field label="Pool">{w.pool ? `${w.pool}, ${w.concurrency} slots` : '—'}</Field>
              <Field label="Up for">{w.uptime != null ? fmtSpan(w.uptime) : 'not answering'}</Field>
              <Field label="Running / held">
                {w.active ?? 0} / {(w.reserved ?? 0) + (w.scheduled ?? 0)}
              </Field>
              <Field label="Done since start">{fmtNum(w.processed)}</Field>
              <Field label="Peak memory (process)">{w.peak_rss_mb ? `${fmtNum(w.peak_rss_mb)} MB` : '—'}</Field>
              {w.metrics && (
                <>
                  <Field label="Memory (container)">
                    <UsagePct pct={w.metrics.memory_pct} />
                  </Field>
                  <Field label="CPU">
                    <UsagePct pct={w.metrics.cpu_pct} />
                  </Field>
                  <Field label="Restarts, last hour">
                    {w.metrics.restarts}
                    {w.metrics.last_restart_at ? `, last ${fmtAgo(w.metrics.last_restart_at)}` : ''}
                  </Field>
                </>
              )}
              <Totals span={d} />
            </div>
          </Card>

          <Card title="Queues" note="a pause lasts until the worker restarts" flush>
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
                <tr>
                  <Th>Queue</Th>
                  <Th>State</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {queues.length === 0 && <EmptyRow cols={3}>This worker consumes no queue.</EmptyRow>}
                {queues.map((queue) => {
                  const consuming = (w.queues ?? []).includes(queue);
                  const deployed = (w.expected_queues ?? []).includes(queue);
                  return (
                    <tr key={queue} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-2">
                        <QueueLink name={queue} env={env} />
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {!w.alive ? (
                          <span className="text-gray-500">worker not answering</span>
                        ) : consuming ? (
                          <span className="text-green-700">consuming{deployed ? '' : ' (added by hand)'}</span>
                        ) : (
                          <span className="font-medium text-amber-700">paused</span>
                        )}
                      </td>
                      <td className="w-12 px-4 py-2 text-right">
                        <RowMenu
                          name={queue}
                          env={env}
                          items={perms.canOperate && w.alive ? [consumeItem(w.name, queue, consuming)] : []}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          <Card title="Held right now" note="refreshes every 15 seconds" flush>
            <HeldTable rows={d.held} env={env} canOperate={perms.canOperate} showWorker={false} />
          </Card>

          <Charts span={d} />
          {d.metrics && (
            <div className="grid gap-3 xl:grid-cols-2">
              <Card title="Memory" note="percent of the container">
                <MetricChart series={d.metrics.memory ?? null} percent spanHours={Math.min(WINDOW_HOURS[window], 168)} />
              </Card>
              <Card title="CPU" note="percent">
                <MetricChart series={d.metrics.cpu ?? null} percent spanHours={Math.min(WINDOW_HOURS[window], 168)} />
              </Card>
            </div>
          )}
          <FiguresCard title={`What it ran, last ${window}`} rows={d.tasks} env={env} by="task" empty="No runs recorded for this worker in this window." />
          <Card title="Recent failures on this worker" flush>
            <FailureList rows={d.failures} env={env} canOperate={perms.canOperate} />
          </Card>
          {w.app_id && w.component &&
            (perms.canLogs ? (
              <LogsCard appId={w.app_id} component={w.component} />
            ) : (
              <Card title="Log">
                <p className="text-sm text-gray-500">Reading a worker&apos;s log takes the cloud.logs permission.</p>
              </Card>
            ))}
          <Card title="Console history" flush>
            <AuditTable rows={d.audit} empty="Nothing has been done to this worker from the console." />
          </Card>
        </>
      )}
    </Detail>
  );
};

/* ---------- queue ---------- */

export const QueueDetailPage: React.FC = () => {
  const { name = '' } = useParams();
  const [env] = useWorkerEnv();
  const [window, setWindow] = useState<WorkerWindow>('24h');
  const perms = useWorkerPerms();
  const q = useWorkersQueueQuery(env, name, window);
  const d = q.data;
  const now = Date.now() / 1000;
  return (
    <Detail
      title={name}
      description="One queue: what is waiting in it, since when, who takes work from it and how long work waits."
      crumb="Queues"
      tab="queues"
      q={q}
      window={window}
      onWindow={setWindow}
      actions={
        perms.canManage && d && d.depth > 0 ? (
          <ChangeButton env={env} change={{ kind: 'purge', queue: name }} icon={<Trash2 className="mr-2 h-3.5 w-3.5" />} danger>
            Purge
          </ChangeButton>
        ) : undefined
      }
    >
      {d && (
        <>
          <HealthCard health={d.queue.health} ok="Consumed, with nothing waiting long." />
          <Card title="Queue">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
              <Field label="Waiting now">{fmtNum(d.depth)}</Field>
              <Field label="Oldest has waited">{d.queue.oldest_age != null ? fmtSpan(d.queue.oldest_age) : d.depth ? 'not stamped' : 'nothing waiting'}</Field>
              <Field label="Consumed by">
                {d.queue.consumers.length
                  ? d.queue.consumers.map((c) => (
                      <span key={c} className="mr-2">
                        <WorkerLink name={c} env={env} />
                      </span>
                    ))
                  : 'no worker'}
              </Field>
              <Totals span={d} />
            </div>
          </Card>
          {d.trend && (
            <Card title="Depth" note="last 24 hours">
              <MetricChart series={[{ component: name, instance: 'Messages waiting', points: d.trend }]} spanHours={24} />
            </Card>
          )}
          <div className="grid gap-3 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <Card title="Waiting, oldest first" note={`${d.messages.length} of ${fmtNum(d.depth)} shown; arguments masked`} flush>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
                      <tr>
                        <Th>Task</Th>
                        <Th right>Waiting</Th>
                        <Th>Task id</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.messages.length === 0 && <EmptyRow cols={3}>Nothing is waiting in this queue.</EmptyRow>}
                      {d.messages.map((m, i) => (
                        <tr key={m.id ?? i} className="border-b border-gray-50 align-top last:border-0">
                          <td className="px-4 py-2">
                            <TaskLink name={m.task} env={env} />
                            {(m.args || m.kwargs) && (
                              <span className="block max-w-md truncate font-mono text-[11px] text-gray-500">
                                {m.args} {m.kwargs}
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-right text-gray-700">
                            {m.sent_at ? fmtSpan(now - m.sent_at) : ''}
                            {m.eta && <span className="block text-[11px] text-gray-500">due {new Date(m.eta).toLocaleString()}</span>}
                            {!!m.retries && <span className="block text-[11px] text-gray-500">try {m.retries + 1}</span>}
                          </td>
                          <td className="px-4 py-2 font-mono text-[11px] text-gray-500">{m.id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
            <Card title="What it is made of" note={d.sampled < d.depth ? `newest ${d.sampled} sampled` : undefined} flush>
              {d.mix.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-gray-400">Nothing waiting.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {d.mix.map((m) => (
                    <li key={m.task} className="flex items-center justify-between gap-3 px-4 py-2">
                      <TaskLink name={m.task} env={env} />
                      <span className="text-sm text-gray-700">{fmtNum(m.count)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
          <Charts span={d} />
          <FiguresCard title={`Tasks that ran from it, last ${window}`} rows={d.tasks} env={env} by="task" empty="No runs recorded from this queue in this window." />
          <Card title="Console history" flush>
            <AuditTable rows={d.audit} empty="Nothing has been done to this queue from the console." />
          </Card>
        </>
      )}
    </Detail>
  );
};

/* ---------- task ---------- */

export const TaskDetailPage: React.FC = () => {
  const { name = '' } = useParams();
  const [env] = useWorkerEnv();
  const [window, setWindow] = useState<WorkerWindow>('24h');
  const perms = useWorkerPerms();
  const q = useWorkersTaskQuery(env, name, window);
  const d = q.data;
  return (
    <Detail
      title={shortTask(name)}
      description={name}
      crumb="Tasks"
      tab="tasks"
      q={q}
      window={window}
      onWindow={setWindow}
      actions={
        d && (
          <>
            {perms.canManage && (
              <ChangeButton env={env} change={{ kind: 'rate_limit', task: name, rate: '' }} icon={<Gauge className="mr-2 h-3.5 w-3.5" />}>
                Rate limit
              </ChangeButton>
            )}
            {perms.canOperate && d.task.runnable && (
              <ChangeButton env={env} change={{ kind: 'run_task', task: name }} icon={<Play className="mr-2 h-3.5 w-3.5" />}>
                Run now
              </ChangeButton>
            )}
          </>
        )
      }
    >
      {d && (
        <>
          <HealthCard health={d.task.health} ok="Registered where it is routed, and not failing." />
          <Card title="Task">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
              <Field label="Queue">{d.task.queue ? <QueueLink name={d.task.queue} env={env} /> : 'not in the routing table'}</Field>
              <Field label="On the schedule as">
                {d.task.scheduled.length ? (
                  <Link to={tabPath('schedule', env)} className="hover:underline">
                    {d.task.scheduled.join(', ')}
                  </Link>
                ) : (
                  'not scheduled'
                )}
              </Field>
              <Field label="Last ran">{d.totals.last_at ? fmtTime(d.totals.last_at) : `not in the last ${window}`}</Field>
              <Totals span={d} />
            </div>
          </Card>
          <Charts span={d} />
          {d.held.length > 0 && (
            <Card title="Held right now" flush>
              <HeldTable rows={d.held} env={env} canOperate={perms.canOperate} />
            </Card>
          )}
          <FiguresCard title={`By worker, last ${window}`} rows={d.workers} env={env} by="worker" empty="No runs recorded for this task in this window." />
          <Card title="Recent failures" flush>
            <FailureList rows={d.failures} env={env} canOperate={perms.canOperate} showTask={false} />
          </Card>
          <Card title="Recent runs" note="kept for about a day" flush>
            <RunsTable rows={d.runs} env={env} showTask={false} />
          </Card>
          <Card title="Console history" flush>
            <AuditTable rows={d.audit} empty="Nothing has been done to this task from the console." />
          </Card>
        </>
      )}
    </Detail>
  );
};
